import React, { Component } from 'react'
import type { DropdownOption } from 'types/ui'
import type { User } from 'types/user'
import { getUserDropdownObject, isValidUser } from '@disco/utils/user'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
type Props = {
  users: User[]
  chosenUsers:
    | ('' | number | DropdownOption | DropdownOption[])
    | null
    | undefined
  onUserSelect: (...args: Array<any>) => any
  usersLoading: boolean | null | undefined
  multi: boolean
  clearable: boolean
  placeholder?: string
  disabled?: boolean
  scrollContainer?: HTMLElement | null
}
/**
 * UserSelector component for selecting users from a dropdown list
 *
 * A React class component that provides a dropdown interface for selecting
 * users. Supports both single and multi-select modes, user validation,
 * and integrates with the CustomDropdown component for consistent UI.
 *
 * @param props - Component props
 * @param props.users - Array of available users to select from
 * @param props.chosenUsers - Currently selected user(s) or user IDs
 * @param props.onUserSelect - Callback function called when users are selected
 * @param props.usersLoading - Whether users are currently loading
 * @param props.multi - Whether multiple users can be selected
 * @param props.clearable - Whether the selection can be cleared
 * @param props.placeholder - Placeholder text for the dropdown
 * @param props.disabled - Whether the selector is disabled
 * @param props.scrollContainer - Container element for fixed positioning (prevents menu from expanding parent)
 * @returns JSX element containing the user selection dropdown
 */
export default class UserSelector extends Component<Props> {
  static defaultProps: {
    clearable: boolean
    multi: boolean
  } = {
    multi: true,
    clearable: false,
  }

  render(): React.ReactNode {
    // filter possible junk users, just in case
    const options = this.props.users
      .filter(user => isValidUser(user))
      .map(user => getUserDropdownObject(user))
    return (
      <CustomDropdown
        options={options}
        multi={this.props.multi}
        clearable={this.props.clearable}
        placeholder={this.props.placeholder}
        value={this.props.chosenUsers}
        onChange={this.props.onUserSelect}
        disabled={this.props.disabled}
        scrollContainer={this.props.scrollContainer}
      />
    )
  }
}
